import clsx from 'clsx'
import React from 'react'
import { CloseIcon } from '../icons'

export interface ChipProps {
  label: string
  variant?: 'filled' | 'outlined'
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'warning'
    | 'info'
  size?: 'small' | 'medium'
  onDelete?: () => void
  onClick?: () => void
  disabled?: boolean
  icon?: React.ReactNode
  className?: string
}

export const Chip: React.FC<ChipProps> = ({
  label,
  variant = 'filled',
  color = 'default',
  size = 'medium',
  onDelete,
  onClick,
  disabled = false,
  icon,
  className = '',
}) => {
  const sizeClasses = {
    small: 'px-2 py-1 text-xs h-6',
    medium: 'px-3 py-1.5 text-sm h-8',
  }

  const getColorClasses = () => {
    const colorMap = {
      filled: {
        default:
          'bg-gray-100 text-gray-800 hover:bg-gray-200 focus:ring-gray-500',
        primary:
          'bg-indigo-500 text-white hover:bg-indigo-600 focus:ring-indigo-500',
        secondary:
          'bg-purple-500 text-white hover:bg-purple-600 focus:ring-purple-500',
        success:
          'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500',
        error: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500',
        warning:
          'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500',
        info: 'bg-cyan-500 text-white hover:bg-cyan-600 focus:ring-cyan-500',
      },
      outlined: {
        default:
          'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
        primary:
          'border border-indigo-500 text-indigo-600 hover:bg-indigo-50 focus:ring-indigo-500',
        secondary:
          'border border-purple-500 text-purple-600 hover:bg-purple-50 focus:ring-purple-500',
        success:
          'border border-green-500 text-green-600 hover:bg-green-50 focus:ring-green-500',
        error:
          'border border-red-500 text-red-600 hover:bg-red-50 focus:ring-red-500',
        warning:
          'border border-yellow-500 text-yellow-600 hover:bg-yellow-50 focus:ring-yellow-500',
        info: 'border border-cyan-500 text-cyan-600 hover:bg-cyan-50 focus:ring-cyan-500',
      },
    }

    return colorMap[variant][color]
  }

  const iconSize = size === 'small' ? 'w-3 h-3' : 'w-4 h-4'
  const deleteIconSize = size === 'small' ? 'w-3 h-3' : 'w-4 h-4'

  return (
    <div
      className={clsx(
        'inline-flex items-center gap-1 font-medium rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
        sizeClasses[size],
        getColorClasses(),
        {
          'opacity-50 cursor-not-allowed pointer-events-none': disabled,
          'cursor-pointer': onClick && !disabled,
        },
        className,
      )}
      onClick={!disabled && onClick ? onClick : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onKeyDown={e => {
        if (onClick && !disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
    >
      {icon && <span className={clsx(iconSize, 'flex-shrink-0')}>{icon}</span>}
      <span className="truncate">{label}</span>
      {onDelete && (
        <button
          type="button"
          className={clsx(
            deleteIconSize,
            'flex-shrink-0 ml-1 hover:bg-black/20 rounded-full p-0.5 transition-colors flex items-center justify-center',
          )}
          onClick={e => {
            e.stopPropagation()
            if (!disabled) onDelete()
          }}
          disabled={disabled}
          aria-label="Delete"
        >
          <CloseIcon size={10} />
        </button>
      )}
    </div>
  )
}
